import type {
  PartnerOrigin,
  PartnerOriginCreate,
  PartnerOriginUpdate,
  PartnerSearchOriginsV1Params,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { LinkAssetPayload, BulkLinkPayloads } from '../api-utils';
import { linkMultipleAssets } from '../api-utils';

type SingleAssetLinkType = 'schedules' | 'contacts' | 'contacts/returnTo' | 'addresses' | 'addresses/returnTo';
type ArrayAssetLinkType = 'tags' | 'laneSortConfiguration';

export const origins = {
  search: {
    queryKey: 'partner-management-service/origins',
    configurationType: 'default' as const,
    fn: async (params: PartnerSearchOriginsV1Params = {}): Promise<PartnerOrigin[]> => {
      const { data } = await GatewayAxiosClient.get<PartnerOrigin[]>(
        '/partner-management-proxy/api/v1/partners/origins',
        { params },
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/origins/id',
    configurationType: 'no-cache' as const,
    fn: async (originId: string): Promise<PartnerOrigin> => {
      const { data } = await GatewayAxiosClient.get<PartnerOrigin>(
        `/partner-management-proxy/api/v1/partners/origins/${originId}`,
      );

      return data;
    },
  },
  create: async (origin: PartnerOriginCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerOrigin>(
      '/partner-management-proxy/api/v1/partners/origins',
      origin,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/origins']] };
  },
  update: async ({ originId, payload }: { originId: string; payload: PartnerOriginUpdate }) => {
    const { data } = await GatewayAxiosClient.put<PartnerOrigin>(
      `/partner-management-proxy/api/v1/partners/origins/${originId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/origins'],
        ['partner-management-service/origins/id', originId],
      ],
    };
  },
  archive: async (originId: string) => {
    await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/origins/${originId}`, {
      active: false,
      archived: true,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/origins'],
        ['partner-management-service/origins/id', originId],
      ],
    };
  },
  link: async ({
    originId,
    payload,
  }: {
    originId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/origins/${originId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/origins'],
        ['partner-management-service/origins/id', originId],
      ],
    };
  },
  bulkLink: async ({
    origin,
    payloads,
  }: {
    origin: PartnerOrigin;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerOrigin>(
      payloads,
      {
        tags: origin.tagIds,
        schedules: origin.scheduleId,
        laneSortConfiguration: origin.laneSortConfigurationIds,
        contacts: origin.contactId,
        'contacts/returnTo': origin.returnToContactId,
        addresses: origin.addressId,
        'addresses/returnTo': origin.returnToAddressId,
      },
      (linkPayload) => origins.link({ originId: origin.originId, payload: linkPayload }),
    );
  },
};
